/**
 * The elements a viewer actually reads, and whether one is legible at the frame it is measured in.
 *
 * Its own module because both the reading-time rules and the shared pose pass they run on need it,
 * and having the pass import it from the rule file would make the two circular.
 */
import { effectiveOpacity, ownElementText } from './measure';

// Below this opacity, a fade's dim frames don't count as legible read time.
const VISIBLE_OPACITY = 0.5;

const NON_RENDERED_TAGS = new Set([
    'SCRIPT',
    'STYLE',
    'TEMPLATE',
    'NOSCRIPT',
    'TITLE',
    'META',
    'LINK',
]);

export interface TextLeaf {
    element: Element;
    chars: number;
    preview: string;
    /**
     * Position in `body.querySelectorAll('*')`, which pairs a leaf with itself across poses. A
     * sandboxed pose returns a different document, and the leaf list is not a stable index into it:
     * an element only counts as a leaf while it renders its own text, so anything that types itself
     * in — the typewriter skill starts from an empty span — joins the list partway through the scene.
     */
    index: number;
}

/** Elements rendering their own copy, minus ambient text (watermarks/motifs), which isn't read. */
export function collectTextLeaves(document: Document): TextLeaf[] {
    const body = document.body;
    if (!body) {
        return [];
    }
    const leaves: TextLeaf[] = [];
    const all = Array.from(body.querySelectorAll('*'));
    all.forEach((element, index) => {
        if (NON_RENDERED_TAGS.has(element.tagName) || element.tagName === 'BR') {
            return;
        }
        if (element.closest('[data-l5-text-role="ambient"]')) {
            return;
        }
        const text = ownElementText(element);
        if (!text) {
            return;
        }
        leaves.push({ element, chars: text.length, preview: text, index });
    });
    return leaves;
}

interface BoxLike {
    left: number;
    right: number;
    top: number;
    bottom: number;
    width: number;
    height: number;
}

// A zero-area box (mid-layout, or jsdom's no-layout rects) gets the benefit of the doubt, so this only
// excludes text genuinely animated off-screen.
function intersectsCanvas(
    box: BoxLike,
    canvas: { width: number; height: number } | undefined,
): boolean {
    if (!canvas) {
        return true;
    }
    if (box.width <= 0 || box.height <= 0) {
        return true;
    }
    return box.left < canvas.width && box.right > 0 && box.top < canvas.height && box.bottom > 0;
}

const INLINE_TAGS = new Set([
    'A',
    'B',
    'EM',
    'I',
    'MARK',
    'SMALL',
    'SPAN',
    'STRONG',
    'SUB',
    'SUP',
    'TSPAN',
    'U',
]);

/** The line a leaf reads as part of: a word styled as its own span is still read with its sentence. */
export function lineOf(element: Element): Element {
    let line = element;
    while (INLINE_TAGS.has(line.tagName.toUpperCase()) && line.parentElement) {
        line = line.parentElement;
    }
    return line;
}

export function textOf(element: Element): string {
    return (element.textContent ?? '').replace(/\s+/g, ' ').trim();
}

/** Whether this leaf is legible in the frame it was collected from: bright enough, and in frame. */
export function isLegible(
    leaf: TextLeaf,
    canvasSize: { width: number; height: number } | undefined,
): boolean {
    return (
        effectiveOpacity(leaf.element) >= VISIBLE_OPACITY &&
        intersectsCanvas(leaf.element.getBoundingClientRect(), canvasSize)
    );
}
